import { Fragment, useState } from "react";
import {
  CommonCodeGroupType,
  useGetCommonCodeGroupsByTypeAsAdminQuery,
} from "../../../lib/apollo/graphql/generated";
import Checkbox from "../../common/components/Checkbox";
import Modal, { ModalProps } from "../../common/components/Modal";
import SelectBox from "../../common/components/SelectBox";
import BasicButton from "../../common/components/BasicButton";
import Loader from "../../common/components/Loader";

const WELFARE_COMMON_CODES = [
  CommonCodeGroupType.WelfareContentCategory,
  CommonCodeGroupType.WelfareContentFamilyType,
  CommonCodeGroupType.WelfareContentLifeCycle,
  CommonCodeGroupType.WelfareContentType,
] as const;

type WelfareCommonCodeType = (typeof WELFARE_COMMON_CODES)[number];
type ActiveCode = {
  id: string;
  name: string;
  code: string;
};

const checkIsWelfareCodeGroup = (
  groupCode?: string
): groupCode is WelfareCommonCodeType => {
  if (
    !groupCode ||
    !WELFARE_COMMON_CODES.includes(groupCode as WelfareCommonCodeType)
  ) {
    return false;
  }

  return true;
};

interface CheckboxGridProps {
  title: string;
  children: React.ReactNode;
}

function CheckboxGrid({ title, children }: CheckboxGridProps) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-lg font-bold">{title}</p>
      <ul className="grid grid-cols-2 gap-1">{children}</ul>
    </div>
  );
}

interface WelfareCategoriesModalProps extends ModalProps {
  onApply: (ids: string[]) => void;
  onClear: () => void;
}

export default function WelfareCategoriesModal({
  open,
  onApply,
  onClose,
  onClear,
}: WelfareCategoriesModalProps) {
  const [ids, setIds] = useState<{ [key in WelfareCommonCodeType]: string[] }>({
    [CommonCodeGroupType.WelfareContentType]: [],
    [CommonCodeGroupType.WelfareContentCategory]: [],
    [CommonCodeGroupType.WelfareContentFamilyType]: [],
    [CommonCodeGroupType.WelfareContentLifeCycle]: [],
  });

  const { data, loading } = useGetCommonCodeGroupsByTypeAsAdminQuery({
    variables: {
      types: [...WELFARE_COMMON_CODES],
    },
  });

  if (loading) {
    return (
      <Modal
        title="복지정보 카테고리"
        height="800px"
        open={open}
        onClose={onClose}
      >
        <Loader />
      </Modal>
    );
  }

  const codes = data?.getCommonCodeGroupsByTypeAsAdmin || [];
  const grouped: {
    [key in WelfareCommonCodeType]?: ActiveCode[];
  } = {};

  for (const code of codes) {
    if (!checkIsWelfareCodeGroup(code.groupCode)) {
      continue;
    }

    grouped[code.groupCode] = [...(code.activeCommonCodes || [])];
  }

  const centralGovtId =
    grouped[CommonCodeGroupType.WelfareContentType]?.find(
      (item) => item.name === "중앙부처"
    )?.id || "";

  const isCentralGovtSelected =
    centralGovtId === ids[CommonCodeGroupType.WelfareContentType][0];

  const energyId =
    grouped[CommonCodeGroupType.WelfareContentCategory]?.find(
      (item) => item.name === "에너지"
    )?.id || "";

  const onIdSelect = (key: WelfareCommonCodeType, value: string) => {
    if (key === CommonCodeGroupType.WelfareContentType) {
      let categories = ids[CommonCodeGroupType.WelfareContentCategory];

      if (value !== centralGovtId) {
        categories = categories.filter((categoryId) => categoryId !== energyId);
      }

      setIds((previous) => ({
        ...previous,
        [CommonCodeGroupType.WelfareContentCategory]: categories,
        [key]: [value],
      }));
    } else {
      setIds((previous) => {
        const keyedArray = previous[key];
        const isIncluded = keyedArray.includes(value);
        const updated = isIncluded
          ? keyedArray.filter((id) => id !== value)
          : [...keyedArray, value];

        return {
          ...previous,
          [key]: updated,
        };
      });
    }
  };

  const clearForm = () => {
    if (!window.confirm("선택한 내용을 전부 취소할까요?")) {
      return;
    }

    setIds({
      [CommonCodeGroupType.WelfareContentType]: [],
      [CommonCodeGroupType.WelfareContentCategory]: [],
      [CommonCodeGroupType.WelfareContentFamilyType]: [],
      [CommonCodeGroupType.WelfareContentLifeCycle]: [],
    });

    onClear();
    onClose();
  };

  return (
    <Modal
      title="복지정보 카테고리"
      height="800px"
      open={open}
      onClose={onClose}
    >
      <div className="flex h-full flex-col gap-3 overflow-y-auto">
        <div className="flex flex-col gap-1">
          <p className="text-lg font-bold">행정 구분</p>
          <SelectBox
            value={ids[CommonCodeGroupType.WelfareContentType][0]}
            onSelect={(typeId) => {
              onIdSelect(CommonCodeGroupType.WelfareContentType, typeId);
            }}
            options={(
              grouped[CommonCodeGroupType.WelfareContentType] || []
            ).map((code) => ({
              label: code.name || "",
              value: code.id || "",
            }))}
          />
        </div>

        {!!ids[CommonCodeGroupType.WelfareContentType][0] && (
          <Fragment>
            <CheckboxGrid title="관심주제">
              {grouped[CommonCodeGroupType.WelfareContentCategory]?.map(
                (code) => {
                  if (!isCentralGovtSelected && code.name === "에너지") {
                    return null;
                  }

                  const {
                    [CommonCodeGroupType.WelfareContentCategory]: categoryIds,
                  } = ids;

                  return (
                    <Checkbox
                      label={code.name}
                      key={code.id}
                      isChecked={categoryIds.includes(code.id)}
                      onCheck={() => {
                        onIdSelect(
                          CommonCodeGroupType.WelfareContentCategory,
                          code.id
                        );
                      }}
                    />
                  );
                }
              )}
            </CheckboxGrid>

            <CheckboxGrid title="생애주기">
              {grouped[CommonCodeGroupType.WelfareContentLifeCycle]?.map(
                (code) => {
                  const {
                    [CommonCodeGroupType.WelfareContentLifeCycle]: lifecycleIds,
                  } = ids;

                  return (
                    <Checkbox
                      label={code.name}
                      key={code.id}
                      isChecked={lifecycleIds.includes(code.id)}
                      onCheck={() => {
                        onIdSelect(
                          CommonCodeGroupType.WelfareContentLifeCycle,
                          code.id
                        );
                      }}
                    />
                  );
                }
              )}
            </CheckboxGrid>

            <CheckboxGrid title="가구상황">
              {grouped[CommonCodeGroupType.WelfareContentFamilyType]?.map(
                (code) => {
                  const {
                    [CommonCodeGroupType.WelfareContentFamilyType]:
                      familyTypeIds,
                  } = ids;

                  return (
                    <Checkbox
                      label={code.name}
                      key={code.id}
                      isChecked={familyTypeIds.includes(code.id)}
                      onCheck={() => {
                        onIdSelect(
                          CommonCodeGroupType.WelfareContentFamilyType,
                          code.id
                        );
                      }}
                    />
                  );
                }
              )}
            </CheckboxGrid>
          </Fragment>
        )}
      </div>
      <div className="flex gap-1">
        <BasicButton
          variant="error"
          text="선택 취소"
          className="w-1/3"
          onClick={clearForm}
        />
        <BasicButton
          text="조건 적용"
          className="w-full"
          onClick={() => {
            onApply([
              ...ids[CommonCodeGroupType.WelfareContentType],
              ...ids[CommonCodeGroupType.WelfareContentCategory],
              ...ids[CommonCodeGroupType.WelfareContentFamilyType],
              ...ids[CommonCodeGroupType.WelfareContentLifeCycle],
            ]);

            onClose();
          }}
        />
      </div>
    </Modal>
  );
}
